import React, { useEffect } from 'react';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useTenderSections } from '../../hooks/useTenderSections';
import { PERMISSIONS } from '../../shared/constants/permissions';
import type { Tender } from '../../types/tender';

import { useSectionAssignments } from './hooks/useSectionAssignments';
import { useSectionCRUD } from './hooks/useSectionCRUD';
import { useSectionDnD } from './hooks/useSectionDnD';
import { useSectionEditing } from './hooks/useSectionEditing';
import { useSectionHistory } from './hooks/useSectionHistory';

export interface UseSectionReviewsOptions {
  tenderId: string;
  tender: Tender;
  account: import('../../types/auth').User | null;
  hasPermission: (perm: string) => boolean;
  actionSectionId?: string | null | undefined;
  onCompletionChange: (isComplete: boolean) => void;
}

export function useSectionReviews({
  tenderId,
  tender,
  account,
  hasPermission,
  actionSectionId,
  onCompletionChange,
}: UseSectionReviewsOptions) {
  const { showSnackbar } = useSnackbar();
  const {
    sections,
    loading,
    updateStatus,
    saveContent,
    resolveSuggestion,
    updateSectionState,
    createSection,
    deleteSection,
    reorderSections,
    lock,
    unlock,
    assign,
  } = useTenderSections(tenderId, account, tender);

  const isOwner = tender.ownerId === account?.uid;
  const hasSystemManage = false;
  const userTenderPermissions = account?.uid ? tender.memberPermissions?.[account.uid] : undefined;

  const checkTenderPerm = (perm: string) => {
    if (userTenderPermissions === undefined) {
      return hasPermission(perm);
    }
    if (Array.isArray(userTenderPermissions)) {
      return userTenderPermissions.includes(perm);
    }
    if (typeof userTenderPermissions === 'object' && userTenderPermissions !== null) {
      return !!(userTenderPermissions as Record<string, boolean>)[perm];
    }
    return false;
  };

  const canEditAllSections = Boolean(
    isOwner || hasSystemManage || checkTenderPerm(PERMISSIONS.DRAFT_EDIT)
  );

  // Shared state
  const [editingSectionId, setEditingSectionId] = React.useState<string | null>(null);
  const [editContent, setEditContent] = React.useState('');
  const [showDiffs, setShowDiffs] = React.useState<Record<string, boolean>>({});

  // 1. Assignments
  const assignments = useSectionAssignments(account, assign);

  // 2. Drag & Drop
  const dnd = useSectionDnD(sections, tenderId, tender, reorderSections);

  // 3. History
  const history = useSectionHistory(
    tenderId,
    saveContent,
    resolveSuggestion,
    updateSectionState,
    editingSectionId,
    setEditContent,
    setShowDiffs,
    dnd.setExpandedSectionIds,
    showSnackbar,
    unlock
  );

  // 4. Editing
  const editing = useSectionEditing(
    account,
    tenderId,
    tender,
    sections,
    saveContent,
    resolveSuggestion,
    updateSectionState,
    history.pushToHistory,
    showSnackbar,
    lock,
    unlock,
    editingSectionId,
    setEditingSectionId,
    editContent,
    setEditContent,
    showDiffs,
    setShowDiffs
  );

  // 5. CRUD
  const crud = useSectionCRUD(
    tenderId,
    tender,
    account,
    createSection,
    deleteSection,
    updateStatus,
    editingSectionId,
    setEditingSectionId,
    editing.setActiveEditor,
    unlock,
    dnd.setExpandedSectionIds
  );

  // Effects mapped from original file
  const prevAllReviewedRef = React.useRef<boolean>(false);
  useEffect(() => {
    const allReviewed = sections.length > 0 && sections.every((s) => s.status === 'reviewed');
    if (prevAllReviewedRef.current !== allReviewed) {
      prevAllReviewedRef.current = allReviewed;
      onCompletionChange(allReviewed);
    }
  }, [sections, onCompletionChange]);

  const hasAutoScrolledRef = React.useRef<string | null>(null);
  useEffect(() => {
    if (
      !loading &&
      sections.length > 0 &&
      actionSectionId &&
      hasAutoScrolledRef.current !== actionSectionId
    ) {
      const targetSection = sections.find((s) => s.id === actionSectionId);
      if (targetSection) {
        hasAutoScrolledRef.current = actionSectionId;
        dnd.setExpandedSectionIds((prev: Set<string>) => {
          const next = new Set(prev);
          next.add(actionSectionId);
          return next;
        });

        setTimeout(() => {
          const element = document.getElementById(`section-${actionSectionId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
      }
    }
  }, [loading, sections, actionSectionId, dnd.setExpandedSectionIds]);

  const prevAssignments = React.useRef<Record<string, string | undefined>>({});
  useEffect(() => {
    if (editingSectionId) {
      const currentSection = sections.find((s) => s.id === editingSectionId);
      if (currentSection) {
        const wasAssignedToMe = prevAssignments.current[editingSectionId] === account?.uid;
        const isAssignedToMe = currentSection.assignedTo?.uid === account?.uid;
        if (wasAssignedToMe && !isAssignedToMe && !isOwner) {
          showSnackbar('Your assignment to this section was removed.', 'warning');
          unlock(currentSection);
          setEditingSectionId(null);
        }
      }
    }

    const newAssignments: Record<string, string | undefined> = {};
    for (const s of sections) {
      newAssignments[s.id] = s.assignedTo?.uid;
    }
    prevAssignments.current = newAssignments;
  }, [sections, editingSectionId, account?.uid, isOwner, unlock, showSnackbar]);

  return {
    sections,
    loading,
    isOwner,
    canEditAllSections,
    unlock,
    assign,
    ...assignments,
    ...dnd,
    ...history,
    ...editing,
    ...crud,
  };
}
